// The stats routes (docs/console-api.md) and their fallback: until the control plane has the hourly
// collector, a project's numbers come from the daily usage/* documents and the mail stats it already
// keeps, shaped the same way, so every page renders either way.
import { collection, getDocs, limit, orderBy, query, where } from "@estra/client/firestore";
import { api, db, missing, myMail } from "../api.ts";
import { lastDays, type Trend } from "../fmt.ts";

export type Range = "24h" | "7d" | "30d" | "90d";
export const RANGES: Range[] = ["24h", "7d", "30d", "90d"];
export const RANGE_DAYS: Record<Range, number> = { "24h": 1, "7d": 7, "30d": 30, "90d": 90 };

export type Metric =
  | "requests" | "errors" | "cpuMs"
  | "docReads" | "docWrites" | "docDeletes" | "queries" | "denied"
  | "rowsRead" | "rowsWritten" | "dbBytes"
  | "users" | "signups" | "activeUsers"
  | "fileBytes" | "fileObjects" | "fileOpsA" | "fileOpsB"
  | "rtOpens" | "rtMinutes" | "rtSnaps" | "rtPeak"
  | "emails" | "emailsFailed" | "emailsBounced" | "emailsComplained"
  | "pushes" | "pushesInvalid" | "pushesFailed"
  | "fnCalls" | "fnErrors";

export interface Fact { dim: string; value: number }
export interface StoryLine { text: string; tone: "good" | "bad" | "neutral"; href: string; projectId?: string }
export interface Milestone { key: string; value: number; reachedAt: number; seen: boolean; projectId?: string }
export interface Cohort { day: string; size: number; d1: number | null; d7: number | null; d30: number | null }

export interface ProjectStats {
  range: Range;
  buckets: string[];
  series: Partial<Record<Metric, number[]>>;
  kpis: Partial<Record<Metric, Trend>>;
  facts: Record<string, Fact[]>;
  cohorts: Cohort[];
  mau: number | null;
  milestones: Milestone[];
  story: StoryLine[];
  allowance: Partial<Record<Metric, number>>;
  updatedAt: number | null;
  sources: { cf: boolean; ae: boolean; tenant: boolean };
  /** Set by the console when the numbers came from the fallback (older control plane). */
  fallback?: boolean;
}
export interface HomeStats {
  days: string[];
  projects: { id: string; series: Partial<Record<"requests" | "activeUsers" | "docWrites" | "docReads", number[]>>; kpis: Partial<Record<Metric, Trend>> }[];
  totals: Partial<Record<Metric, number>>;
  story: StoryLine[];
  milestones: Milestone[];
  fallback?: boolean;
}
export interface NowStats {
  at: number;
  perMinute: number[];
  connected: number;
  activeToday: number;
  errorsLastHour: number;
  live: boolean;
}

/** Daily allowances during the beta (settled question 1): shown, not enforced. */
export const BETA_ALLOWANCE: Partial<Record<Metric, number>> = { docReads: 50_000, docWrites: 20_000, docDeletes: 20_000, emails: 1000 };

const sum = (a: number[] | undefined) => (a ?? []).reduce((s, v) => s + v, 0);
const median = (a: number[]) => {
  const s = [...a].sort((x, y) => x - y);
  return s.length % 2 ? s[(s.length - 1) / 2]! : (s[s.length / 2 - 1]! + s[s.length / 2]!) / 2;
};

/** now / prev / usual for a daily series covering 2 × range days (oldest first). */
function trendOf(daily: number[], days: number, gauge = false): Trend {
  const now = daily.slice(-days);
  const prev = daily.slice(-2 * days, -days);
  const complete = daily.slice(-8, -1);
  const usual = complete.length >= 4 ? median(complete) * (gauge ? 1 : days) : null;
  return gauge
    ? { now: now[now.length - 1] ?? 0, prev: prev[prev.length - 1] ?? 0, usual: null }
    : { now: sum(now), prev: sum(prev), usual, week: sum(daily.slice(-7)) };
}

interface UsageDoc { day: string; requests?: number; rowsRead?: number; rowsWritten?: number; dbBytes?: number }

async function usageDays(uid: string, id: string, n: number): Promise<Map<string, UsageDoc>> {
  const s = await getDocs(query(collection(db, "usage"), where("ownerUid", "==", uid), where("projectId", "==", id), orderBy("day", "desc"), limit(n))).catch(() => null);
  return new Map((s?.docs ?? []).map((d) => [String(d.data().day), d.data() as UsageDoc]));
}

/** ProjectStats built from what older control planes keep: usage/* (daily) and mail stats. */
export async function fallbackStats(uid: string, id: string, range: Range): Promise<ProjectStats> {
  const days = Math.max(RANGE_DAYS[range], 7);
  const span = lastDays(days * 2 + 1);
  const [usage, mail] = await Promise.all([
    usageDays(uid, id, span.length),
    myMail(id)(`stats?days=${Math.min(span.length, 90)}`).then((r) => r.data as { day: string; sent?: number; failed?: number; bounced?: number; complained?: number }[]).catch(() => []),
  ]);
  const mailBy = new Map(mail.map((m) => [m.day, m]));
  const col = (f: (day: string) => number) => span.map(f);
  const daily: Partial<Record<Metric, number[]>> = {
    requests: col((d) => usage.get(d)?.requests ?? 0),
    rowsRead: col((d) => usage.get(d)?.rowsRead ?? 0),
    rowsWritten: col((d) => usage.get(d)?.rowsWritten ?? 0),
    dbBytes: col((d) => usage.get(d)?.dbBytes ?? 0),
    emails: col((d) => mailBy.get(d)?.sent ?? 0),
    emailsFailed: col((d) => mailBy.get(d)?.failed ?? 0),
    emailsBounced: col((d) => mailBy.get(d)?.bounced ?? 0),
    emailsComplained: col((d) => mailBy.get(d)?.complained ?? 0),
  };
  // dbBytes is a gauge: carry the last known value over days without a record.
  let last = 0;
  daily.dbBytes = daily.dbBytes!.map((v) => (v ? (last = v) : last));
  const shown = RANGE_DAYS[range] === 1 ? 7 : RANGE_DAYS[range];
  const kpis: Partial<Record<Metric, Trend>> = {};
  for (const [k, v] of Object.entries(daily) as [Metric, number[]][]) kpis[k] = trendOf(v, RANGE_DAYS[range] === 1 ? 1 : RANGE_DAYS[range], k === "dbBytes");
  return {
    range,
    buckets: span.slice(-shown),
    series: Object.fromEntries(Object.entries(daily).map(([k, v]) => [k, v!.slice(-shown)])),
    kpis,
    facts: {},
    cohorts: [],
    mau: null,
    milestones: [],
    story: [],
    allowance: BETA_ALLOWANCE,
    updatedAt: usage.size ? Date.now() : null,
    sources: { cf: usage.size > 0, ae: false, tenant: false },
    fallback: true,
  };
}

export async function projectStats(uid: string, id: string, range: Range): Promise<ProjectStats> {
  try {
    return await api<ProjectStats>(`/v1/stats/${encodeURIComponent(id)}?range=${range}`);
  } catch (e) {
    if (missing(e)) return fallbackStats(uid, id, range);
    throw e;
  }
}

export async function homeStats(uid: string, ids: string[]): Promise<HomeStats> {
  try {
    return await api<HomeStats>("/v1/stats/home");
  } catch (e) {
    if (!missing(e)) throw e;
    const days = lastDays(14);
    const each = await Promise.all(ids.map((id) => fallbackStats(uid, id, "7d").then((s) => ({ id, s }))));
    const totals: Partial<Record<Metric, number>> = {};
    for (const { s } of each) for (const k of ["requests", "emails"] as Metric[]) totals[k] = (totals[k] ?? 0) + (s.kpis[k]?.now ?? 0);
    return {
      days,
      projects: each.map(({ id, s }) => {
        const pad = (a: number[] | undefined) => [...Array(Math.max(0, 14 - (a?.length ?? 0))).fill(0), ...(a ?? [])].slice(-14);
        return { id, series: { requests: pad(s.series.requests) }, kpis: s.kpis };
      }),
      totals,
      story: [],
      milestones: [],
      fallback: true,
    };
  }
}

export async function nowStats(id: string): Promise<NowStats | null> {
  try {
    return await api<NowStats>(`/v1/stats/${encodeURIComponent(id)}/now`);
  } catch (e) {
    if (missing(e)) return null;
    throw e;
  }
}

export const MILESTONES: Record<string, { label: string; hint: string }> = {
  "first.doc": { label: "First document", hint: "Your backend stored its first document." },
  "first.user": { label: "First user", hint: "Someone signed up." },
  "first.email": { label: "First email", hint: "Your app sent its first email." },
  "first.push": { label: "First push", hint: "Your app sent its first notification." },
  "first.realtime": { label: "First live client", hint: "A client listened in realtime." },
  "first.push_config": { label: "First estra push", hint: "You changed your schema or rules from your repository." },
  "users.10": { label: "10 users", hint: "Ten people have signed up." },
  "users.100": { label: "100 users", hint: "A hundred people have signed up." },
  "users.1k": { label: "1,000 users", hint: "A thousand people." },
  "users.10k": { label: "10,000 users", hint: "Ten thousand people." },
  "reads.100k": { label: "100k reads", hint: "Documents read, all time." },
  "reads.1m": { label: "1M reads", hint: "A million documents read." },
  "reads.10m": { label: "10M reads", hint: "Ten million documents read." },
  "best.dau": { label: "Best day", hint: "Most active users in one day." },
  "best.concurrent": { label: "Most at once", hint: "Most clients connected at the same time." },
  "streak.writes7": { label: "7-day streak", hint: "Writes every day for a week." },
};
export const milestoneLabel = (m: Milestone) => {
  const def = MILESTONES[m.key];
  if (!def) return m.key;
  return m.key.startsWith("best.") ? `${def.label}: ${m.value.toLocaleString("en-US")}` : def.label;
};
